import { Dialog, DialogContent } from "@/components/ui/dialog"
import { useEffect, useState } from "react"
import { getMemberById } from "@/modules/Users/service/UsersService"
import { MemberDetailType } from "@/types/membros"
import { useLoading } from "@/hooks/useLoading"
import { User } from "lucide-react"
import { Button } from "../../../components/ui/button"

interface MemberModalProps {
  isOpen: boolean
  onClose: () => void
  memberId: string | number | undefined
}

export function MemberModal({ isOpen, onClose, memberId }: MemberModalProps) {
  const [member, setMember] = useState<MemberDetailType | null>(null)
  const { showLoading, hideLoading } = useLoading()

  useEffect(() => {
    if (!isOpen || !memberId) return
    setMember(null)
    showLoading()
    getMemberById(String(memberId))
      .then(({ data }) => {
        setMember(data.data ?? data)
      })
      .catch((error) => {
        console.error("Error fetching member details:", error)
      })
      .finally(() => {
        hideLoading()
      })
  }, [isOpen, memberId])

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onClose}
    >
      <DialogContent hideCloseButton>
        {member ? (
          <main>
            <div className="flex flex-wrap justify-between">
              <div className="flex basis-[350px] gap-4">
                {member.pesFotoUrl ? (
                  <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-surface text-3xl">
                    <img
                      src={member.pesFotoUrl}
                      alt={member.pesNome}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : (
                  <User size={96} />
                )}
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-bold">{member.pesNome}</h2>
                  {member.pesCidade && (
                    <p className="text-muted-foreground">{`${member.pesCidade} - ${member.pesEstado}`}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                {member.pesTelefone && (
                  <a
                    href={`https://wa.me/55${member.pesTelefone}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex size-12 items-center justify-center gap-3 rounded-full bg-muted/20 transition-colors hover:bg-muted/30"
                  >
                    <i className="fi fi-brands-whatsapp flex items-center justify-center text-2xl text-green-500" />
                  </a>
                )}
                {member.pesInstagram && (
                  <a
                    href={member.pesInstagram}
                    target="_blank"
                    rel="noreferrer"
                    className="flex size-12 items-center justify-center gap-3 rounded-full bg-muted/20 transition-colors hover:bg-muted/30"
                  >
                    <i className="fi fi-brands-instagram flex items-center justify-center text-2xl text-pink-500" />
                  </a>
                )}
              </div>
            </div>

            <div className="my-6 flex items-center">
              <hr className="w-full" />
              <span className="text-nowrap rounded-full border px-3 py-1 text-sm uppercase text-muted">
                Empresa
              </span>
              <hr className="w-full" />
            </div>

            <div className="flex justify-between">
              <div className="flex flex-col gap-4">
                <div className="flex min-w-[200px] gap-2">
                  {member.empLogoUrl && (
                    <img
                      src={member.empLogoUrl}
                      alt={member.empFantasia}
                      className="size-10 rounded-sm border object-cover"
                    />
                  )}
                  <div className="flex flex-col">
                    <p>{member.empFantasia}</p>
                    <div className="flex min-w-[200px] items-center gap-2 text-sm text-muted">
                      <i className="fi fi-ts-marker flex items-center" />
                      <p>{`${member.empCidade} - ${member.empEstado}`}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  {member.catpDescricao && (
                    <div className="flex items-center justify-center rounded-full bg-purple-900/50 px-3 py-1 text-center">
                      <span className="text-xs text-purple-500">{member.catpDescricao}</span>
                    </div>
                  )}
                  {member.catsDescricao && (
                    <div className="flex items-center justify-center rounded-full bg-green-900/50 px-3 py-1 text-center">
                      <span className="text-xs text-green-500">{member.catsDescricao}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-end">
                <h6 className="text-xl font-bold">{member.empNumeroFuncionarios}</h6>
                <span>Funcionários</span>
              </div>
            </div>

            <div className="flex w-full justify-end pt-4">
              <Button
                className="w-full"
                variant="secondary"
                onClick={onClose}
              >
                Fechar
              </Button>
            </div>
          </main>
        ) : (
          <div>Carregando...</div>
        )}
      </DialogContent>
    </Dialog>
  )
}
